import {
  LATEST_LONGFORM_TYPES,
  YOUTUBE_PUBLISHED_AT,
} from './home'
import type { ContentItem } from './types'
import { HANDOFF_CONTENTS } from './handoffContent'
import { SITE } from './site'

/** Watch URL for a content item; falls back to channel only if no video is set. */
export function youtubeWatchUrl(videoId?: string) {
  const id = videoId?.trim()
  return id ? `https://www.youtube.com/watch?v=${id}` : SITE.youtubeUrl
}

function withYoutubeDates(items: ContentItem[]): ContentItem[] {
  return items.map((item) => ({
    ...item,
    youtubePublishedAt: item.youtubePublishedAt ?? YOUTUBE_PUBLISHED_AT[item.id],
  }))
}

/**
 * Phase 1 library with real Deiveega Oosai YouTube video IDs.
 * Update youtubeVideoId when a newer/better upload should be featured.
 */
const CONTENTS_RAW: ContentItem[] = [
  {
    id: 'lingashtakam',
    slug: 'lingashtakam-tamil-lyrics-meaning',
    type: 'traditional-stuti',
    titleTa: 'லிங்காஷ்டகம்',
    titleEn: 'Lingashtakam',
    deity: 'shiva',
    featured: true,
    shortIntroTa:
      'அஷ்டவக்ர முனிவரால் இயற்றப்பட்டதாகக் கருதப்படும் சிவலிங்க ஸ்தோத்திரம். எட்டு சுலோகங்களில் சிவனின் லிங்க வடிவத்தை வணங்கும் பாரம்பரிய ஸ்துதி.',
    occasionTa: 'தினசரி சிவ வழிபாடு, திங்கட்கிழமை, மகா சிவராத்திரி, பிரதோஷம்',
    originTa: 'பாரம்பரிய சமஸ்கிருத ஸ்தோத்திரம். தமிழ் உச்சரிப்பு மற்றும் எளிய பொருளுடன் வழங்கப்பட்டுள்ளது.',
    youtubeVideoId: 'pfW4uJ3iaPw',
    keywords: ['லிங்காஷ்டகம்', 'lingashtakam', 'shiva', 'சிவன்', 'sivan lyrics'],
    seoDescription: 'லிங்காஷ்டகம் தமிழ் வரிகள், உச்சரிப்பு மற்றும் எளிய பொருள் — தெய்வீக ஓசை.',
    pdfUrl: '/downloads/lingashtakam.pdf',
    wallpaperIds: ['wp-shiva-1', 'wp-shiva-2'],
    relatedIds: ['vaidyanatha-ashtakam', 'aigiri-nandini'],
    verses: [
      {
        original: 'ब्रह्ममुरारिसुरार्चितलिङ्गं निर्मलभासितशोभितलिङ्गम् ।\nजन्मजदुःखविनाशकलिङ्गं तत् प्रणमामि सदाशिवलिङ्गम् ॥',
        tamil: 'பிரஹ்மமுராரி சுரார்ச்சித லிங்கம்\nநிர்மல பாஸித சோபித லிங்கம்\nஜன்மஜ துக்க விநாசக லிங்கம்\nதத் ப்ரணமாமி சதாசிவ லிங்கம்',
        meaningTa: 'பிரம்மன், விஷ்ணு மற்றும் தேவர்களால் வழிபடப்படும், தூய்மையாக ஒளிரும் சிவலிங்கத்தை வணங்குகிறேன் — பிறவித் துன்பத்தை நீக்கும் சதாசிவ லிங்கம்.',
        translitEn: 'Brahma Murari Surarchita Lingam… Tat Pranamami Sadashiva Lingam',
      },
      {
        original: 'देवमुनिप्रवरार्चितलिङ्गं कामदहं करुणाकरलिङ्गम् ।\nरावणदर्पविनाशनलिङ्गं तत् प्रणमामि सदाशिवलिङ्गम् ॥',
        tamil: 'தேவமுனி ப்ரவரார்ச்சித லிங்கம்\nகாமதஹம் கருணாகர லிங்கம்\nராவண தர்ப விநாசன லிங்கம்\nதத் ப்ரணமாமி சதாசிவ லிங்கம்',
        meaningTa: 'தேவர்களும் முனிவர்களும் வழிபடும், காமத்தை அழித்து கருணை காட்டும், ராவணனின் அகங்காரத்தை அடக்கிய லிங்கத்தை வணங்குகிறேன்.',
      },
      {
        original: 'सर्वसुगन्धिसुलेपितलिङ्गं बुद्धिविवर्धनकारणलिङ्गम् ।\nसिद्धसुरासुरवन्दितलिङ्गं तत् प्रणमामि सदाशिवलिङ्गम् ॥',
        tamil: 'ஸர்வ சுகந்தி சுலேபித லிங்கம்\nபுத்தி விவர்தன காரண லிங்கம்\nசித்த சுராசுர வந்தித லிங்கம்\nதத் ப்ரணமாமி சதாசிவ லிங்கம்',
        meaningTa: 'நறுமணப் பொருட்களால் அலங்கரிக்கப்பட்ட, அறிவை வளர்க்கும், சித்தர்கள் தேவர்கள் அசுரர்களால் வணங்கப்படும் லிங்கத்தை வணங்குகிறேன்.',
      },
      {
        original: 'कनकमहामणिभूषितलिङ्गं फणिपतिवेष्टितशोभितलिङ्गम् ।\nदक्षसुयज्ञविनाशनलिङ्गं तत् प्रणमामि सदाशिवलिङ्गम् ॥',
        tamil: 'கனக மகாமணி பூஷித லிங்கம்\nபணிபதி வேஷ்டித சோபித லிங்கம்\nதக்ஷசு யஜ்ஞ விநாசன லிங்கம்\nதத் ப்ரணமாமி சதாசிவ லிங்கம்',
        meaningTa: 'தங்கமும் மாணிக்கங்களும் அணிந்த, நாகாபரணத்தால் அழகுபெற்ற, தக்கன் யாகத்தை அழித்த லிங்கத்தை வணங்குகிறேன்.',
      },
    ],
  },
  {
    id: 'aigiri-nandini',
    slug: 'aigiri-nandini-tamil-lyrics-meaning',
    type: 'traditional-stuti',
    titleTa: 'ஐகிரி நந்தினி',
    titleEn: 'Aigiri Nandini',
    deity: 'amman',
    featured: true,
    shortIntroTa:
      'தேவி மகிஷாசுரமர்தினியைப் போற்றும் புகழ்பெற்ற சமஸ்கிருத ஸ்துதி. ஆடி வெள்ளி மற்றும் நவராத்திரி காலத்தில் பரவலாகப் பாடப்படும்.',
    occasionTa: 'நவராத்திரி, ஆடி வெள்ளி, தேவி வழிபாடு',
    originTa: 'ஆதி சங்கரருக்குச் சேர்க்கப்படும் மஹிஷாசுரமர்தினி ஸ்தோத்திரம்.',
    youtubeVideoId: '-IfPr5ICd9g',
    keywords: ['ஐகிரி நந்தினி', 'aigiri nandini', 'amman', 'அம்மன்', 'navratri'],
    seoDescription: 'ஐகிரி நந்தினி தமிழ் வரிகள் மற்றும் பொருள் — தெய்வீக ஓசை.',
    wallpaperIds: ['wp-amman-1'],
    relatedIds: ['amman-potri-108', 'lingashtakam'],
    verses: [
      {
        tamil: 'ஐகிரி நந்தினி நந்தித மேதினி\nவிஸ்வ வினோதினி நந்தனுதே\nகிரிவர விந்திய சிரோதினி வாஸினி\nவிஷ்ணு விலாஸினி ஜிஷ்ணுனுதே',
        meaningTa: 'மலையரசியே, உலகை மகிழ்விப்பவளே, விந்திய சிகரத்தில் வாழ்பவளே, விஷ்ணுவின் ஆனந்தமூட்டும் சக்தியே — உன்னை வணங்குகிறேன்.',
      },
      {
        tamil: 'பகவதி ஹே சிதிகண்டகுடும்பினி\nபூரித குண்டலி பூரிதனுதே\nஜய ஜய ஹே மஹிஷாசுர மர்தினி\nரம்யகபர்தினி ஸைலசுதே',
        meaningTa: 'சிவனின் மனைவியே, குண்டலினியால் நிறைந்தவளே, மகிஷாசுரனை வென்றவளே, மலையரசி மகளே — ஜெய ஜெய.',
      },
    ],
  },
  {
    id: 'aintu-karathanai',
    slug: 'ainthu-karathanai-vinayagar-lyrics',
    type: 'traditional-stuti',
    titleTa: 'ஐந்து கரத்தனை',
    titleEn: 'Ainthu Karathanai',
    deity: 'vinayagar',
    shortIntroTa: 'விநாயகரைப் போற்றும் பரவலான தமிழ் பக்திப் பாடல். காலையில் இறை வணக்கத்திற்கு ஏற்றது.',
    occasionTa: 'தினசரி காலை வழிபாடு, விநாயகர் சதுர்த்தி',
    youtubeVideoId: '11-5Qvz61lw',
    keywords: ['ஐந்து கரத்தனை', 'vinayagar', 'விநாயகர்', 'ganesha'],
    seoDescription: 'ஐந்து கரத்தனை விநாயகர் பாடல் வரிகள் — தெய்வீக ஓசை.',
    relatedIds: ['ganesha-pancharatnam', 'vinayagar-mantras', 'vinayagar-potri-108'],
    verses: [
      {
        tamil: 'ஐந்து கரத்தனை ஆனை முகத்தனை\nஇந்தின் இளம்பிறை போலும் எயிற்றனை\nநந்தி மகன்தனை ஞானக் கொழுந்தினைப்\nபந்தனையாழ்வார் பார் புகழ் வேண்டியே',
        meaningTa: 'ஐந்து கைகளையும் யானைமுகத்தையும் கொண்ட, நிலவின் வளைவைப் போன்ற தந்தங்களையுடைய, நந்தியின் மைந்தன், ஞானக்கொழுந்து — உலகத்தார் தொழுது புகழும் விநாயகரை வணங்குகிறோம்.',
      },
    ],
  },
  {
    id: 'uruvai-aruvai',
    slug: 'uruvai-aruvai-kandar-anuboothi',
    type: 'traditional-stuti',
    titleTa: 'உருவாய் அருவாய்',
    titleEn: 'Uruvai Aruvai — Kandar Anuboothi',
    deity: 'murugan',
    shortIntroTa:
      'அருணகிரிநாதரின் கந்தர் அனுபூதியிலிருந்து. உருவம் உள்ளவனாகவும் உருவமற்றவனாகவும் முருகனை உணரும் அனுபவப் பாடல்.',
    occasionTa: 'தினசரி முருகன் வழிபாடு, கந்த சஷ்டி',
    originTa: 'அருணகிரிநாதர் — கந்தர் அனுபூதி',
    youtubeVideoId: '5g85Zrvj9f4',
    keywords: ['உருவாய் அருவாய்', 'kandar anuboothi', 'murugan', 'முருகன்', 'arunagirinathar'],
    seoDescription: 'உருவாய் அருவாய் — கந்தர் அனுபூதி தமிழ் வரிகள் மற்றும் பொருள்.',
    relatedIds: ['muthai-tharu', 'aintu-karathanai'],
    verses: [
      {
        tamil: 'உருவாய் அருவாய் உளதாய் இலதாய்\nமருவாய் மலராய் மணியாய் ஒளியாய்\nகருவாய் உயிராய் கதியாய் விதியாய்\nகுருவாய் வருவாய் அருள்வாய் குகனே',
        meaningTa: 'உருவமாகவும் அருவமாகவும், உள்ளதாகவும் இல்லாததாகவும், மணமாகவும் மலராகவும், மணியாகவும் ஒளியாகவும், கருவாகவும் உயிராகவும், கதியாகவும் விதியாகவும் — குருவாக வந்து அருள்வாயாக, குகனே.',
      },
    ],
  },
  {
    id: 'muthai-tharu',
    slug: 'muthai-tharu-thiruppugazh',
    type: 'traditional-stuti',
    titleTa: 'முத்தைத் தரு',
    titleEn: 'Muthai Tharu — Thiruppugazh',
    deity: 'murugan',
    shortIntroTa: 'அருணகிரிநாதரின் திருப்புகழ். முருகனின் அருளை வேண்டும் இசை நயமுள்ள பாசுரம்.',
    occasionTa: 'முருகன் வழிபாடு, கந்த சஷ்டி',
    originTa: 'அருணகிரிநாதர் — திருப்புகழ்',
    youtubeVideoId: 'xOwsIZATwsA',
    keywords: ['முத்தைத் தரு', 'muthai tharu', 'thiruppugazh', 'murugan'],
    seoDescription: 'முத்தைத் தரு திருப்புகழ் வரிகள் மற்றும் பொருள் — தெய்வீக ஓசை.',
    relatedIds: ['uruvai-aruvai'],
    verses: [
      {
        tamil: 'முத்தைத் தரு பத்தித் திருநகை\nஅத்திக்கிறை சத்திச் சரவண\nமுத்தித் தெரு வைத்த பகவனை\nஉற்றொருவன் யார்…',
        meaningTa: 'முத்தைப் போன்ற புன்னகையும், சக்தியின் கதிரையும் கொண்ட சரவணனின் திருவடிகளில் முக்திப் பாதையை அமைத்த பகவானை அடைந்தவர் யார்?',
      },
    ],
  },
  {
    id: 'amman-potri-108',
    slug: '108-amman-potri',
    type: 'potri',
    titleTa: '108 அம்மன் பொற்றி',
    titleEn: '108 Amman Potri',
    deity: 'amman',
    shortIntroTa: 'அம்மனின் 108 நாமங்களைப் பொற்றிப் பாடும் தொகுப்பு. குழு வழிபாடு மற்றும் நவராத்திரிக்கு ஏற்றது.',
    occasionTa: 'நவராத்திரி, ஆடி வெள்ளி, அம்மன் வழிபாடு',
    youtubeVideoId: 'rUyKoF209Xg',
    keywords: ['108 amman potri', 'அம்மன் பொற்றி', 'amman', 'potri'],
    seoDescription: '108 அம்மன் பொற்றி — தெய்வீக ஓசை.',
    wallpaperIds: ['wp-amman-1'],
    relatedIds: ['aigiri-nandini'],
    verses: [
      {
        tamil: 'ஓம் சக்தி பொற்றி\nஓம் பராசக்தி பொற்றி\nஓம் ஆதிபராசக்தி பொற்றி\nஓம் துர்க்கை பொற்றி\nஓம் லக்ஷ்மி பொற்றி\nஓம் சரஸ்வதி பொற்றி',
        meaningTa: 'அம்மனின் பல்வேறு நாமங்களைப் பொற்றி வணங்குதல் — சக்தி, பராசக்தி, துர்க்கை, லக்ஷ்மி, சரஸ்வதி.',
      },
    ],
  },
  {
    id: 'vinayagar-mantras',
    slug: 'five-vinayagar-mantras',
    type: 'mantra',
    titleTa: 'ஐந்து விநாயகர் மந்திரங்கள்',
    titleEn: 'Five Vinayagar Mantras',
    deity: 'vinayagar',
    shortIntroTa: 'தினசரி உச்சரிக்க ஏற்ற ஐந்து எளிய விநாயகர் மந்திரங்கள். உச்சரிப்பு மற்றும் சுருக்கமான பொருளுடன்.',
    occasionTa: 'காலை வழிபாடு, புதிய தொடக்கம், விநாயகர் சதுர்த்தி',
    youtubeVideoId: '-vFfKWqI_Is',
    keywords: ['vinayagar mantra', 'விநாயகர் மந்திரம்', 'ganesha mantra'],
    seoDescription: 'ஐந்து விநாயகர் மந்திரங்கள் — தமிழ் உச்சரிப்பு மற்றும் பொருள்.',
    relatedIds: ['ganesha-pancharatnam', 'runa-vimochana-ganapati', 'vinayagar-potri-108'],
    verses: [
      {
        tamil: 'ஓம் கம் கணபதயே நம:\nஓம் வக்ரதுண்டாய நம:\nஓம் ஏகதந்தாய நம:\nஓம் விக்கின ராஜாய நம:\nஓம் சித்தி விநாயகாய நம:',
        meaningTa: 'கணபதி, வக்ரதுண்டர், ஏகதந்தர், விக்கினராஜர், சித்தி விநாயகர் — இவர்களின் நாமங்களில் வணக்கம்.',
      },
    ],
  },
  {
    id: 'ayyappan-song',
    slug: 'ayyappan-devotional-song',
    type: 'original-song',
    titleTa: 'ஐயப்பன் பக்திப் பாடல்',
    titleEn: 'Ayyappan Devotional Song',
    deity: 'ayyappan',
    isOriginalDO: true,
    featured: true,
    shortIntroTa: 'சபரிமலை பக்திக்காக தெய்வீக ஓசை உருவாக்கிய அசல் பக்திப் பாடல்.',
    themeTa: 'சரணம் அய்யப்பா என்ற பக்தி உணர்வு மற்றும் மன அமைதி.',
    occasionTa: 'சபரிமலை காலம், மண்டல காலம்',
    youtubeVideoId: 'KNTVhKppV8Q',
    keywords: ['ஐயப்பன்', 'ayyappan', 'sabarimala', 'சபரிமலை'],
    seoDescription: 'ஐயப்பன் அசல் பக்திப் பாடல் — தெய்வீக ஓசை.',
    wallpaperIds: ['wp-ayyappan-1'],
    relatedIds: ['mahalakshmi-song'],
    verses: [
      {
        tamil: 'சரணம் அய்யப்பா சரணம் அய்யப்பா\nசபரிமலை வாழும் சுவாமியே\nகுருவாய் வருவாய் அருள்வாய் ஐயனே\nபக்தர் மனம் நீ நிறைவாய்',
        meaningTa: 'சபரிமலை வாழும் ஐயப்பனுக்கு சரணம். குருவாக வந்து அருள்வாயாக; பக்தரின் மனதில் நீ நிறைவாயாக.',
      },
    ],
  },
  {
    id: 'mahalakshmi-song',
    slug: 'mahalakshmi-devotional-song',
    type: 'original-song',
    titleTa: 'மகாலக்ஷ்மி பக்திப் பாடல்',
    titleEn: 'Mahalakshmi Devotional Song',
    deity: 'lakshmi',
    isOriginalDO: true,
    shortIntroTa: 'லக்ஷ்மி தேவியைப் போற்றும் தெய்வீக ஓசை அசல் பாடல்.',
    themeTa: 'செழிப்பு, நன்றியுணர்வு மற்றும் அமைதியான வாழ்க்கை வேண்டுதல்.',
    occasionTa: 'வெள்ளிக்கிழமை, வரலக்ஷ்மி விரதம், தீபாவளி',
    youtubeVideoId: 'N9QkVTVhg_M',
    keywords: ['மகாலக்ஷ்மி', 'mahalakshmi', 'lakshmi', 'லக்ஷ்மி'],
    seoDescription: 'மகாலக்ஷ்மி அசல் பக்திப் பாடல் — தெய்வீக ஓசை.',
    wallpaperIds: ['wp-lakshmi-1'],
    relatedIds: ['mahalakshmi-ashtakam', 'kanakadhara-stotram', 'ayyappan-song'],
    verses: [
      {
        tamil: 'மஹாலக்ஷ்மி ஜெய மஹாலக்ஷ்மி\nஅன்னையே அருள்வாய் அம்பிகையே\nசெல்வம் செழிக்க செந்தாமரைத் தாயே\nமன அமைதி தா தாயே',
        meaningTa: 'மகாலக்ஷ்மிக்கு ஜெயம். செழிப்பும் மன அமைதியும் அருளும் தாயை வேண்டுகிறோம்.',
      },
    ],
  },
  ...HANDOFF_CONTENTS,
]

export const CONTENTS: ContentItem[] = withYoutubeDates(CONTENTS_RAW)

/** Long-form website pages only, newest YouTube publish date first. Excludes DT. */
export function getLatestLongform(limit = 6): ContentItem[] {
  const allowed = new Set<string>(LATEST_LONGFORM_TYPES)
  return CONTENTS.filter(
    (c) =>
      allowed.has(c.type) &&
      Boolean(c.youtubeVideoId?.trim()) &&
      Boolean(c.youtubePublishedAt) &&
      Boolean(c.slug),
  )
    .slice()
    .sort((a, b) => (b.youtubePublishedAt ?? '').localeCompare(a.youtubePublishedAt ?? ''))
    .slice(0, limit)
}

export function contentHref(item: Pick<ContentItem, 'slug' | 'publicPath'>) {
  return item.publicPath ?? `/content/${item.slug}`
}

export function getContentBySlug(slug: string) {
  return CONTENTS.find((c) => c.slug === slug)
}

export function getContentById(id: string) {
  return CONTENTS.find((c) => c.id === id)
}

export function getContentsByDeity(deityId: string) {
  return CONTENTS.filter((c) => c.deity === deityId || c.deities?.includes(deityId as never))
}

export function getContentsByType(types: ContentItem['type'][]) {
  return CONTENTS.filter((c) => types.includes(c.type))
}

export function searchContents(query: string) {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return CONTENTS.filter((c) => {
    const hay = [
      c.titleTa,
      c.titleEn,
      c.shortIntroTa,
      c.occasionTa ?? '',
      ...c.keywords,
      ...c.verses.flatMap((v) => [v.tamil, v.meaningTa, v.translitEn ?? '']),
    ]
      .join(' ')
      .toLowerCase()
    return hay.includes(q)
  })
}
